import { useEffect, useRef, useState } from 'react'
import { useLocation, useParams } from 'wouter'

import { ErrorBoundary } from '@/components/ErrorBoundary'
import { Spinner } from '@/components/Spinner'
import { CreateLinkInput } from '@/components/CreateLinkInput'
import { Button } from '@/components/Button'
// Public links temporarily disabled
// import { PublicLink } from '@/components/PublicLink'
import { useForm, ValidationRules } from '@/hooks/useForm'
import { useEditLink } from '@/hooks/useEditLink'
import { useLinkById } from '@/hooks/useLinkById'
import { useToast } from '@/hooks/Toast'
import { slugValidation, destinationValidation } from '@/utils/validations'
import { EnterIcon } from '@/icons'

import s from './EditLink.module.css'

export type FormValues = {
  slug: string
  destinations: string[]
  teamIds?: { label: string; value: string }[]
}

const validationRules: ValidationRules = {
  slug: slugValidation,
  destinations: {
    dynamicValidation: destinationValidation,
  },
}

export const EditLink = () => {
  const { linkId } = useParams()
  const [, setLocation] = useLocation()
  const [linkFromStorage, setLinkFromStorage] = useState<any>(null)
  const { data: link } = useLinkById(linkId)
  const formRef = useRef<HTMLFormElement>(null)
  const editLinkMutation = useEditLink()
  // Public links temporarily disabled
  // const [publicLink, setPublicLink] = useState('')
  const toast = useToast()

  useEffect(() => {
    const storedLink = localStorage.getItem('editingLink')
    if (storedLink) {
      setLinkFromStorage(JSON.parse(storedLink))
      localStorage.removeItem('editingLink')
    }
  }, [])

  const linkData = link || linkFromStorage

  const {
    values,
    errors,
    isSubmitting,
    handleChange,
    handleDynamicChange,
    addDynamicField,
    removeDynamicField,
    setValues,
    handleSubmit,
  } = useForm({
    initialValues: {
      slug: linkData?.slug ?? '',
      destinations: linkData?.destinations ?? [''],
    } as FormValues,
    validationRules: validationRules,
    formRef: formRef,
  })

  useEffect(() => {
    if (linkData) {
      setValues({
        slug: linkData.slug,
        destinations: linkData.destinations,
      })
    }
  }, [linkData])

  const onSubmit = async (formValues: FormValues) => {
    try {
      const urls = formValues.destinations

      const cleanedUrls = urls.map((url) => {
        const hasProtocol =
          url.startsWith('https://') || url.startsWith('http://')
        return hasProtocol ? url : `https://${url}`
      })

      let _data: any = {
        slug: formValues.slug,
        destinations: cleanedUrls,
      }

      // Public links temporarily disabled
      // if (publicLink) {
      //   const match = publicLink.match(/\?(.*)$/)
      //   const code = match ? match[1] : undefined

      //   _data = {
      //     ..._data,
      //     publicLinkPasscode: code,
      //   }
      // }

      await editLinkMutation.mutateAsync({
        linkId: linkId!,
        payload: _data,
      })
      setLocation('/')
    } catch {
      toast.error('Something went wrong. Try again?')
    }
  }

  if (!linkData) {
    return <Spinner />
  }

  return (
    <ErrorBoundary>
      <form
        className={s.form}
        onSubmit={handleSubmit((values) => onSubmit(values))}
        ref={formRef}
        noValidate
      >
        <CreateLinkInput
          onChange={handleChange}
          onUrlChange={handleDynamicChange}
          errors={errors}
          values={values}
          slug={linkData.slug}
          addDynamicField={addDynamicField}
          removeDynamicField={removeDynamicField}
          defaultValues={{
            slug: linkData.slug,
            destinations: linkData.destinations,
          }}
        />

        {/* Public links temporarily disabled
        <PublicLink
          slug={linkData.slug}
          onUpdatePublicLink={setPublicLink}
          linkId={linkId}
        /> */}

        <footer className={s.footer}>
          <Button variant="ghost" onClick={() => setLocation('/')}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            iconRight={EnterIcon}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Updating...' : 'Update'}
          </Button>
        </footer>
      </form>
    </ErrorBoundary>
  )
}
